import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Button,
  DataTable,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Input,
  Search,
  Checkbox,
  SpinnerLoader,
  Toastify,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { debounce } from '@lift-admin/utils/debounce';
import apiClient from '@lift-admin/utils/apiClient';
import { diffList } from '@lift-admin/utils/logChangeFormatter';
import { actionLog } from '../../../../App';

interface ShowPub {
  showEditBrandPubDialog: any;
  setShowEditBrandPubDialog: any;
  brandInfo: any;
  setBrandInfoChange: any;
  brandInfoChange: any;
}

const ShowPub = ({
  showEditBrandPubDialog,
  setShowEditBrandPubDialog,
  brandInfo,
  setBrandInfoChange,
  brandInfoChange,
}: ShowPub) => {
  const [publishers, setPublishers] = useState<any[]>([]);
  const [publishersBackup, setPublishersBackup] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [notSelected, setNotSelected] = useState<string[]>([]);
  const [hideEditBrandWeightProgressDialog, setHideEditBrandWeightProgressDialog] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const getPublishers = async () => {
    if (!brandInfo?.brand) return;
    
    try {
      setLoading(true);
      const res = await apiClient.get(
        `/affcrm/v1/user/pub/all?brand=${brandInfo?.brand}`
      );

      let results = [];
      for (let i = 0; i < res.data.response.length; i++) {
        results.push({
          pub: res.data.response[i]?.Publisher,
          checked: !brandInfo?.publishers?.includes(
            res.data.response[i]?.Publisher
          ),
        });
      }
      setPublishers(results);
      setPublishersBackup(results);
      setPageIndex(0);
    } catch (error: any) {
      Toastify('error', error.response?.data?.message || 'Failed to fetch publishers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showEditBrandPubDialog && brandInfo) {
    getPublishers();
    }
  }, [brandInfo, showEditBrandPubDialog]);

  useEffect(() => {
    if (brandInfo?.publishers?.length > 0) {
      setNotSelected(brandInfo?.publishers?.split(','));
    } else {
      setNotSelected([]);
    }
  }, [brandInfo?.publishers]);

  const updatePublishers = async () => {
    try {
      setHideEditBrandWeightProgressDialog(false);
      const res = await apiClient.put(`/affcrm/v1/admin/brand/pub/update`, {
        brand: brandInfo?.brand,
        pub: notSelected.toString(),
      });

      if (res.status === 200) {
        setShowEditBrandPubDialog(false);
        setBrandInfoChange(!brandInfoChange);

        const oldExcludedPubs = brandInfo?.publishers
          ? brandInfo.publishers.split(',').filter(Boolean)
          : [];
        const pubDiff = diffList(oldExcludedPubs, notSelected);
        const pubChanges = [];
        if (pubDiff.added.length || pubDiff.removed.length) {
          pubChanges.push({
            label: 'Excluded Publishers',
            added: pubDiff.added,
            removed: pubDiff.removed,
          });
        }
        const desc = {
          message: `Publisher preferences updated for brand "${brandInfo?.brand}"`,
          category: 'brand',
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          brandName: brandInfo?.brand,
          changes: pubChanges,
          details: [
            {
              label: 'Excluded Publishers',
              value: notSelected.length ? notSelected.join(', ') : 'None',
            },
          ],
        };

        await actionLog?.actionLogTrack(JSON.stringify(desc), false);

        Toastify('success', 'Publisher preferences updated!');
        setNotSelected([]);
      } else {
        Toastify('error', 'Something went wrong');
      }
      setHideEditBrandWeightProgressDialog(true);
    } catch (error: any) {
      console.log(error);
      if (error.response) {
        Toastify('error', error.response.data.message);
      } else {
        Toastify('error', 'Something went wrong!');
      }

      const errorDesc = {
        message: `Failed to update publisher preferences for brand ${brandInfo?.brand}`,
        category: 'brand',
        error: error.response?.data?.message || error.message,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      setHideEditBrandWeightProgressDialog(true);
    }
  };

  // Search results
  const searchResults = (query: any) => {
    const results = publishersBackup.filter((info: any) =>
      info?.pub?.toLowerCase()?.includes(query.toLowerCase())
    );
    setPublishers(results);
    setPageIndex(0);
  };

  const searchOptimized = useCallback(debounce(searchResults, 600), [
    publishersBackup,
  ]);

  const handleCheckboxChange = useCallback((pubName: string, currentChecked: boolean) => {
    const newChecked = !currentChecked;

    setPublishers((prev) =>
      prev.map((p: any) =>
        p.pub === pubName ? { ...p, checked: newChecked } : p
      )
    );
    setPublishersBackup((prev) =>
      prev.map((p: any) =>
        p.pub === pubName ? { ...p, checked: newChecked } : p
      )
    );
    setNotSelected((prev) => {
      const next = [...prev];
      if (currentChecked) {
        next.push(pubName);
      } else {
        const idx = next.indexOf(pubName);
        if (idx > -1) next.splice(idx, 1);
      }
      return next;
    });
  }, []);

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return publishers.slice(start, start + pageSize);
  }, [publishers, pageIndex, pageSize]);

  const dataTableColumns = useMemo((): TableColumn<any>[] => [
    {
      key: 'pub',
      header: 'Publisher Name',
      accessor: (row: any) => row.pub,
      render: (_v: any, row: any) => <div className="font-medium">{row.pub}</div>,
    },
    {
      key: 'control',
      header: 'Control',
      accessor: (row: any) => row.pub,
      width: '120px',
      align: 'center' as const,
      render: (_v: any, row: any) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={!!row.checked}
            onCheckedChange={() => handleCheckboxChange(row.pub, !!row.checked)}
          />
        </div>
      ),
    },
  ], [handleCheckboxChange]);

  return (
    <Sheet
      open={showEditBrandPubDialog}
      onOpenChange={(open) => {
        if (!open) {
          setShowEditBrandPubDialog(false);
          setNotSelected([]);
        }
      }}
    >
      <SheetContent
        side="right"
        className="w-[700px] sm:max-w-[700px] flex flex-col h-full overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>
            Edit Brand Publisher for <span className="font-bold">{brandInfo?.brand}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          <div className="space-y-4 p-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search publishers..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  searchOptimized(e.target.value);
                }}
                className="pl-10 w-full"
              />
            </div>

            {/* Table */}
            <div className="flex-1 flex flex-col min-h-0">
              <DataTable
                className="flex-1 min-h-0 overflow-auto"
                maxHeight="100%"
                columns={dataTableColumns}
                data={paginatedData}
                loading={loading}
                emptyMessage={searchText ? 'No publishers found matching your search.' : 'No publishers found.'}
                stickyHeader
                stickyFooter
                showActions={false}
                pagination={{
                  currentPage: pageIndex + 1,
                  totalPages: Math.max(1, Math.ceil(publishers.length / pageSize)),
                  pageSize,
                  totalItems: publishers.length,
                  onPageChange: (p) => setPageIndex(Math.max(0, p - 1)),
                  onPageSizeChange: (size) => { setPageSize(size); setPageIndex(0); },
                  pageSizeOptions: [10, 20, 25, 50, 100, 200],
                }}
              />
            </div>
          </div>
        </div>

        <SheetFooter className="border-t pt-4 mt-4">
            <Button
            variant="outline"
            onClick={() => {
              setShowEditBrandPubDialog(false);
              setNotSelected([]);
            }}
          >
              Cancel
            </Button>
          <Button
            onClick={updatePublishers}
            disabled={!hideEditBrandWeightProgressDialog}
          >
            {hideEditBrandWeightProgressDialog ? 'Update' : <SpinnerLoader size={16} />}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ShowPub;
